import { get } from 'lodash';
import * as React from 'react';

import { Size } from '../../typings/common';
import {
    CanvasViewType,
    PcodeCanvasDisclaimer,
    PcodeCanvasItem,
    PcodeCanvasOldDisclaimer,
    PcodeRenderDataCanvas
} from '../../typings/pcode';

import { mcodeVer } from './McodeVersion';
import { PcodeLoader, PcodeRendererFrame } from './Pcode';

interface CanvasPcodeFrameParams {
    element: string | React.ReactNode;
    type: CanvasViewType;
    version?: number;
    params: PcodeRenderDataCanvas;
    mcodever?: number;
}

type InitCallbacks = { onRender: () => void; onUpdate: () => void };

export class PcodeRendererCanvas {
    private elementId: string | React.ReactNode;
    private frame: PcodeRendererFrame;

    private hasAnimation: boolean;
    private nextData: PcodeRenderDataCanvas;

    private onRenderCallback: () => void;
    private onUpdateCallback: () => void;

    // грузится скрипт pcode или создается iframe или обновляется iframe
    private isLoadingInProgress: boolean = false;
    private isLoadingNotStarted: boolean = true;

    constructor(elementId: string | React.ReactNode, callbacks: InitCallbacks) {
        this.elementId = elementId;
        this.onRenderCallback = callbacks.onRender;
        this.onUpdateCallback = callbacks.onUpdate;
    }

    public updatePreviewData(data: PcodeRenderDataCanvas, hasAnimation: boolean): void {
        this.hasAnimation = hasAnimation;

        if (this.isLoadingNotStarted) {
            return this.initPcodeLoader(data);
        }

        if (this.isLoadingInProgress) {
            this.nextData = data;

            return;
        }

        this.update(data);
    }

    private initPcodeLoader(data: PcodeRenderDataCanvas): void {
        this.isLoadingInProgress = true;
        this.isLoadingNotStarted = false;

        PcodeLoader.loadScript().then(() => {
            // если пока скрипт грузился упели прийти новые данные для pcode
            // то инициализируем pcode уже с новыми данными
            this.initFrameAPI(this.nextData || data);

            delete this.nextData;
        });
    }

    private initFrameAPI(data: PcodeRenderDataCanvas): void {
        const frameData = this.prepareFrameParams(data);

        const FrameAPI = get(window, 'Ya.mediaCode.FrameAPI');

        if (!FrameAPI) {
            throw new Error('Ya.mediaCode.FrameAPI is not defined');
        }

        this.frame = new FrameAPI(
            Object.assign(frameData, {
                callbacks: {
                    onRender: this.onRender,
                    onUpdate: this.onUpdate
                }
            })
        );
    }

    private update(data: PcodeRenderDataCanvas): void {
        this.isLoadingInProgress = true;
        const iframeSize = this.size;

        if (data.width !== iframeSize.width || data.height !== iframeSize.height) {
            this.size = {
                width: data.width,
                height: data.height
            };
        }

        const dataWithNewDisclaimer = this.getDataWithNewDisclaimer(data);
        const frameData = this.prepareFrameParams(dataWithNewDisclaimer);
        this.frame.update(frameData.params);
    }

    private onRender = (): void => {
        if (this.nextData) {
            this.update(this.nextData);
            delete this.nextData;
        } else {
            this.onRenderCallback();
            this.animateAfterUpdate();
            this.isLoadingInProgress = false;
        }
    };

    private onUpdate = (): void => {
        if (this.nextData) {
            this.update(this.nextData);
            delete this.nextData;
        } else {
            this.onUpdateCallback();
            this.animateAfterUpdate();
            this.isLoadingInProgress = false;
        }
    };

    private get size(): Size {
        return {
            width: this.frame.iFrame.width,
            height: this.frame.iFrame.height
        };
    }

    private set size(size: Size) {
        this.frame.setSizeOfIFrame({ params: size });
    }

    private prepareFrameParams(data: PcodeRenderDataCanvas): CanvasPcodeFrameParams {
        return {
            element: this.elementId,
            type: data.bundle.name,
            version: data.bundle.version,
            params: data,
            mcodever: mcodeVer
        };
    }

    private animateAfterUpdate(): void {
        if (this.hasAnimation) {
            setTimeout(() => {
                if (this.frame) {
                    this.frame.postMessage(JSON.stringify({ action: 'pcode:visible' }));
                }
            }, 200);
        }
    }

    private getDataWithNewDisclaimer(data: PcodeRenderDataCanvas): PcodeRenderDataCanvas {
        const { elements } = data;
        const newElements: PcodeCanvasItem[] = [];

        for (let i = 0; i < elements.length; i++) {
            if (elements[i].type === 'disclaimer') {
                newElements.push(this.getNewDisclaimer(elements[i] as PcodeCanvasOldDisclaimer));
            } else {
                newElements.push(elements[i]);
            }
        }

        return {
            ...data,
            elements: newElements
        };
    }

    private getNewDisclaimer(disclaimer: PcodeCanvasOldDisclaimer): PcodeCanvasDisclaimer {
        return {
            ...disclaimer,
            options: disclaimers[disclaimer.options.disclaimerId]
        };
    }
}

const disclaimers = {
    2: {
        bannerFlags: 'medicine',
        lang: '1'
    },
    5: {
        bannerFlags: 'medicine',
        lang: '2'
    },
    6: {
        bannerFlags: 'abortion',
        lang: '2'
    },
    8: {
        bannerFlags: 'medicine',
        lang: '5'
    },
    10: {
        bannerFlags: 'medicine',
        lang: '4'
    },
    70: {
        bannerFlags: 'alcohol',
        lang: '2'
    },
    73: {
        bannerFlags: 'dietarysuppl',
        lang: '1'
    },
    75: {
        bannerFlags: 'dietarysuppl',
        lang: '2'
    },
    77: {
        bannerFlags: 'dietarysuppl',
        lang: '4'
    },
    79: {
        bannerFlags: 'dietarysuppl',
        lang: '5'
    },
    82: {
        bannerFlags: 'pseudoweapon',
        lang: '1'
    },
    84: {
        bannerFlags: 'pseudoweapon',
        lang: '4'
    },
    86: {
        bannerFlags: 'pseudoweapon',
        lang: '5'
    },
    113: {
        bannerFlags: 'baby_food_0',
        lang: '1'
    },
    114: {
        bannerFlags: 'baby_food_1',
        lang: '1'
    },
    115: {
        bannerFlags: 'baby_food_2',
        lang: '1'
    },
    116: {
        bannerFlags: 'baby_food_3',
        lang: '1'
    },
    117: {
        bannerFlags: 'baby_food_4',
        lang: '1'
    },
    118: {
        bannerFlags: 'baby_food_5',
        lang: '1'
    },
    119: {
        bannerFlags: 'baby_food_6',
        lang: '1'
    },
    120: {
        bannerFlags: 'baby_food_7',
        lang: '1'
    },
    121: {
        bannerFlags: 'baby_food_8',
        lang: '1'
    },
    122: {
        bannerFlags: 'baby_food_9',
        lang: '1'
    },
    123: {
        bannerFlags: 'baby_food_10',
        lang: '1'
    },
    124: {
        bannerFlags: 'baby_food_11',
        lang: '1'
    },
    125: {
        bannerFlags: 'baby_food_12',
        lang: '1'
    },
    127: {
        bannerFlags: 'baby_food_0',
        lang: '2'
    },
    128: {
        bannerFlags: 'baby_food_1',
        lang: '2'
    },
    129: {
        bannerFlags: 'baby_food_2',
        lang: '2'
    },
    130: {
        bannerFlags: 'baby_food_3',
        lang: '2'
    },
    131: {
        bannerFlags: 'baby_food_4',
        lang: '2'
    },
    132: {
        bannerFlags: 'baby_food_5',
        lang: '2'
    },
    133: {
        bannerFlags: 'baby_food_6',
        lang: '2'
    },
    134: {
        bannerFlags: 'baby_food_7',
        lang: '2'
    },
    135: {
        bannerFlags: 'baby_food_8',
        lang: '2'
    },
    136: {
        bannerFlags: 'baby_food_9',
        lang: '2'
    },
    137: {
        bannerFlags: 'baby_food_10',
        lang: '2'
    },
    138: {
        bannerFlags: 'baby_food_11',
        lang: '2'
    },
    139: {
        bannerFlags: 'baby_food_12',
        lang: '2'
    },
    141: {
        bannerFlags: 'baby_food_0',
        lang: '4'
    },
    142: {
        bannerFlags: 'baby_food_1',
        lang: '4'
    },
    143: {
        bannerFlags: 'baby_food_2',
        lang: '4'
    },
    144: {
        bannerFlags: 'baby_food_3',
        lang: '4'
    },
    145: {
        bannerFlags: 'baby_food_4',
        lang: '4'
    },
    146: {
        bannerFlags: 'baby_food_5',
        lang: '4'
    },
    147: {
        bannerFlags: 'baby_food_6',
        lang: '4'
    },
    148: {
        bannerFlags: 'baby_food_7',
        lang: '4'
    },
    149: {
        bannerFlags: 'baby_food_8',
        lang: '4'
    },
    150: {
        bannerFlags: 'baby_food_9',
        lang: '4'
    },
    151: {
        bannerFlags: 'baby_food_10',
        lang: '4'
    },
    152: {
        bannerFlags: 'baby_food_11',
        lang: '4'
    },
    153: {
        bannerFlags: 'baby_food_12',
        lang: '4'
    },
    155: {
        bannerFlags: 'baby_food_0',
        lang: '5'
    },
    156: {
        bannerFlags: 'baby_food_1',
        lang: '5'
    },
    157: {
        bannerFlags: 'baby_food_2',
        lang: '5'
    },
    158: {
        bannerFlags: 'baby_food_3',
        lang: '5'
    },
    159: {
        bannerFlags: 'baby_food_4',
        lang: '5'
    },
    160: {
        bannerFlags: 'baby_food_5',
        lang: '5'
    },
    161: {
        bannerFlags: 'baby_food_6',
        lang: '5'
    },
    162: {
        bannerFlags: 'baby_food_7',
        lang: '5'
    },
    163: {
        bannerFlags: 'baby_food_8',
        lang: '5'
    },
    164: {
        bannerFlags: 'baby_food_9',
        lang: '5'
    },
    165: {
        bannerFlags: 'baby_food_10',
        lang: '5'
    },
    166: {
        bannerFlags: 'baby_food_11',
        lang: '5'
    },
    167: {
        bannerFlags: 'baby_food_12',
        lang: '5'
    }
};
